import { addSeconds } from 'date-fns';
import dateFormat from 'date-fns-tz/format';
import { type TFunction } from 'i18next';

import { type ClassRecording } from '../classroom/types';

export const getRecordingName = (t: TFunction, recording?: ClassRecording) => {
    if (!recording) {
        return '';
    }
    if (recording.name?.trim().length) {
        return recording.name.trim();
    }

    const recordingDate = new Date(recording.when);

    return t('lti:recordings.default-title', {
        interpolation: { escapeValue: false },
        date: `${recordingDate.toLocaleDateString()}, ${recordingDate.toLocaleTimeString()}`
    });
};

export const recordingTimes = (recording: ClassRecording) => {
    const hasDuration = recording.duration && Number(recording.duration) > 0;
    const startDate = new Date(recording.when);
    const endDate = hasDuration ? addSeconds(new Date(recording.when), recording.duration) : null;
    let startDateStr = dateFormat(startDate, 'PPPP');
    let endDateStr = endDate ? dateFormat(endDate, 'PPPP') : null;
    let date = startDateStr;
    if (startDateStr !== endDateStr && endDate !== null) {
        // The recording spans two dates!
        // Use short date formats for range.
        startDateStr = dateFormat(startDate, 'P');
        endDateStr = dateFormat(endDate, 'P');
        date = `${startDateStr} - ${endDateStr}`;
    }
    const endTimeStr = endDate !== null ? dateFormat(endDate, 'p') : null;
    const times = [dateFormat(startDate, 'p'), endTimeStr].filter(Boolean).join(' - ');
    return { date, times };
};

type RecordingDuration = {
    hoursStr: string;
    minutesStr: string;
    secondsStr: string;
};
export const recordingDuration = (recording: ClassRecording): undefined | RecordingDuration => {
    if (!recording.duration) {
        return undefined;
    }
    // https://codingbeautydev.com/blog/javascript-convert-seconds-to-hours-and-minutes/

    // Note - we are calculating hours, minutes and seconds this way instead of using dateFns because it is
    // possible that a recording could go beyond 24 hours.
    const totalSeconds = recording.duration;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const seconds = (totalSeconds % 60).toFixed(0);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const secondsStr = `${seconds}`.padStart(2, '0');
    const hoursStr = `${hours}`.padStart(2, '0');
    const minutesStr = `${minutes}`.padStart(2, '0');

    return { hoursStr, minutesStr, secondsStr };
};
